import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Login.css';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { Button, IconButton, Drawer, List, ListItem, ListItemText } from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import BarChartIcon from '@mui/icons-material/BarChart';
import SchoolIcon from '@mui/icons-material/School';
import PublicIcon from '@mui/icons-material/Public';
import GroupIcon from '@mui/icons-material/Group';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import BoltIcon from '@mui/icons-material/Bolt';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

interface LoginProps {
  onLogin: (credentialResponse: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (!clientId) {
    return (
      <div className="landing-page">
        <div className="landing-error">
          <h2>{t('login.configurationError')}</h2>
          <p>{t('login.configErrorMessage')}</p>
        </div>
      </div>
    );
  }

  const handleGoogleSuccess = (credentialResponse: any) => {
    console.log('Google login success:', credentialResponse);
    onLogin(credentialResponse);
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'how-it-works', label: t('landing.nav.howItWorks') },
    { id: 'why-classroom-bridge', label: t('landing.nav.whyClassroomBridge') },
    { id: 'solutions', label: t('landing.nav.solutions') },
    { id: 'resources', label: t('landing.nav.resources') },
    { id: 'pilots', label: t('landing.nav.pilots') },
  ];

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <main className="landing-page" role="main" aria-label={t('landing.pageTitle')}>
        {/* Professional sticky header navigation */}
        <header className="landing-header" role="banner">
          <div className="header-container">
            <div className="header-content">
              {/* Navigation Links - Desktop */}
              <nav className="header-nav-desktop" aria-label={t('landing.nav.mainNavigation')}>
                {navLinks.map((link) => (
                  <button
                    key={link.id}
                    className="nav-link"
                    onClick={() => scrollToSection(link.id)}
                    aria-label={link.label}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>

              {/* CTA Buttons */}
              <div className="header-actions">
                <button
                  className="sign-in-link"
                  onClick={() => scrollToSection('hero')}
                  aria-label={t('landing.nav.signIn')}
                >
                  {t('landing.nav.signIn')}
                </button>
                <button
                  className="demo-button-header"
                  onClick={() => window.open('mailto:info@classroombridge.com?subject=Request a Demo', '_blank')}
                  aria-label={t('landing.nav.requestDemo')}
                >
                  {t('landing.nav.requestDemo')}
                </button>
                <IconButton
                  className="mobile-menu-button"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label={t('landing.nav.openMenu')}
                >
                  <MenuIcon />
                </IconButton>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Navigation Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          className="mobile-drawer"
        >
          <div className="mobile-drawer-content">
            <div className="mobile-drawer-header">
              <IconButton onClick={() => setMobileMenuOpen(false)} aria-label={t('common.close')}>
                <CloseIcon />
              </IconButton>
            </div>
            <List>
              {navLinks.map((link) => (
                <ListItem key={link.id} onClick={() => scrollToSection(link.id)} component="button">
                  <ListItemText primary={link.label} />
                </ListItem>
              ))}
            </List>
          </div>
        </Drawer>

        {/* Hero Section with human-centered visuals */}
        <section className="hero-section" id="hero" aria-labelledby="hero-heading">
          <div className="hero-background-blur hero-blur-1" aria-hidden="true" />
          <div className="hero-background-blur hero-blur-2" aria-hidden="true" />

          <div className="hero-container">
            <div className="hero-grid">
              {/* Left: Logo + Headline + CTA */}
              <div className="hero-content-left">
                <img
                  src="/images/CB_Logo_TM.png"
                  alt={t('landing.logoAlt')}
                  className="hero-logo"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="hero-logo-fallback" style={{ display: 'none' }}>
                  <SchoolIcon sx={{ fontSize: 56, color: '#3b82f6' }} />
                  <span className="hero-logo-text">{t('login.title')}</span>
                </div>
                
                <h1 id="hero-heading" className="hero-title">
                  {t('landing.hero.titlePart1')}{' '}
                  <span className="hero-title-gradient">
                    {t('landing.hero.titlePart2')}
                  </span>
                </h1>
                
                <p className="hero-description">{t('landing.hero.description')}</p>

                <div className="hero-actions">
                  <div className="google-login-hero" aria-label={t('landing.hero.signInLabel')}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      theme="outline"
                      size="large"
                      text="signin_with"
                      shape="rectangular"
                    />
                  </div>
                  <button
                    className="demo-button"
                    onClick={() => window.open('mailto:info@classroombridge.com?subject=Schedule a Demo', '_blank')}
                    aria-label={t('landing.hero.scheduleDemo')}
                  >
                    {t('landing.hero.scheduleDemo')}
                  </button>
                </div>

                <div className="hero-student-link">
                  <p>{t('login.studentQuestion')}</p>
                  <Button
                    variant="text"
                    onClick={() => navigate('/student/login')}
                    aria-label={t('login.studentLogin')}
                    sx={{ color: '#3b82f6', fontWeight: 600 }}
                  >
                    {t('login.studentLogin')}
                  </Button>
                </div>
              </div>

              {/* Right: Human-centered imagery with illustration overlays */}
              <div className="hero-image-container">
                <div className="hero-image-wrapper">
                  <img
                    src="/images/diverse-teacher-with-students-in-modern-classroom.jpg"
                    alt={t('landing.hero.heroImageAlt')}
                    className="hero-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  <div className="hero-image-placeholder" style={{ display: 'none' }}>
                    <SchoolIcon sx={{ fontSize: 120, color: 'rgba(59,130,246,0.3)' }} />
                  </div>
                </div>
                <div className="hero-floating-card hero-card-reading animate-bounce-slow">
                  <div className="floating-card-content">
                    <MenuBookIcon className="floating-icon floating-icon-blue" />
                    <span className="floating-text">{t('landing.hero.readingLevel')}</span>
                  </div>
                </div>
                <div className="hero-floating-card hero-card-language animate-float">
                  <div className="floating-card-content">
                    <PublicIcon className="floating-icon floating-icon-green" />
                    <span className="floating-text">{t('landing.hero.languages')}</span>
                  </div>
                </div>
                <div className="hero-floating-card hero-card-differentiated animate-pulse-slow">
                  <div className="floating-card-content">
                    <TrackChangesIcon className="floating-icon floating-icon-purple" />
                    <span className="floating-text">{t('landing.hero.differentiated')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Classroom Bridge Works - Human-centered alternating sections */}
        <section className="why-works-section" id="why-classroom-bridge" aria-labelledby="why-works-heading">
          <div className="section-container">
            <div className="section-header">
              <h2 id="why-works-heading" className="section-title">{t('landing.whyWorks.title')}</h2>
              <p className="section-description">{t('landing.whyWorks.subtitle')}</p>
            </div>

            {/* Section 1: Automatic Differentiation */}
            <div className="alternating-section">
              <div className="alternating-image order-first-mobile">
                <img
                  src="/images/teacher-reviewing-adapted-content-on-tablet.jpg"
                  alt={t('landing.whyWorks.differentiation.imageAlt')}
                  className="section-image"
                />
              </div>
              <div className="alternating-content">
                <span className="content-badge content-badge-blue">{t('landing.whyWorks.differentiation.badge')}</span>
                <h3 className="content-title">{t('landing.whyWorks.differentiation.title')}</h3>
                <p className="content-description">{t('landing.whyWorks.differentiation.description')}</p>
                <div className="checklist">
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.differentiation.check1')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.differentiation.check2')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.differentiation.check3')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Multilingual Support */}
            <div className="alternating-section alternating-reverse">
              <div className="alternating-content">
                <span className="content-badge content-badge-purple">{t('landing.whyWorks.multilingual.badge')}</span>
                <h3 className="content-title">{t('landing.whyWorks.multilingual.title')}</h3>
                <p className="content-description">{t('landing.whyWorks.multilingual.description')}</p>
                <div className="checklist">
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.multilingual.check1')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.multilingual.check2')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.multilingual.check3')}</p>
                  </div>
                </div>
              </div>
              <div className="alternating-image">
                <img
                  src="/images/diverse-students-collaborating-with-technology.jpg"
                  alt={t('landing.whyWorks.multilingual.imageAlt')}
                  className="section-image"
                />
              </div>
            </div>

            {/* Section 3: Google Classroom Integration */}
            <div className="alternating-section">
              <div className="alternating-image order-first-mobile">
                <img
                  src="/images/google-classroom-integration-dashboard.jpg"
                  alt={t('landing.whyWorks.integration.imageAlt')}
                  className="section-image"
                />
              </div>
              <div className="alternating-content">
                <span className="content-badge content-badge-green">{t('landing.whyWorks.integration.badge')}</span>
                <h3 className="content-title">{t('landing.whyWorks.integration.title')}</h3>
                <p className="content-description">{t('landing.whyWorks.integration.description')}</p>
                <div className="checklist">
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.integration.check1')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.integration.check2')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.integration.check3')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Teacher Time Savings */}
            <div className="alternating-section alternating-reverse">
              <div className="alternating-content">
                <span className="content-badge content-badge-yellow">{t('landing.whyWorks.timeSavings.badge')}</span>
                <h3 className="content-title">{t('landing.whyWorks.timeSavings.title')}</h3>
                <p className="content-description">{t('landing.whyWorks.timeSavings.description')}</p>
                <div className="checklist">
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.timeSavings.check1')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.timeSavings.check2')}</p>
                  </div>
                  <div className="checklist-item">
                    <CheckCircleIcon className="check-icon" />
                    <p>{t('landing.whyWorks.timeSavings.check3')}</p>
                  </div>
                </div>
              </div>
              <div className="alternating-image">
                <img
                  src="/images/smiling-teacher-at-desk-with-laptop.jpg"
                  alt={t('landing.whyWorks.timeSavings.imageAlt')}
                  className="section-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works - Illustrated step-by-step */}
        <section className="how-it-works-section" id="how-it-works" aria-labelledby="how-it-works-heading">
          <div className="section-container">
            <div className="section-header">
              <h2 id="how-it-works-heading" className="section-title">{t('landing.howItWorks.title')}</h2>
              <p className="section-description">{t('landing.howItWorks.subtitle')}</p>
            </div>

            <div className="steps-grid">
              <div className="step-card">
                <div className="step-icon step-icon-blue">
                  <UploadIcon />
                </div>
                <h3 className="step-title">{t('landing.howItWorks.steps.upload.title')}</h3>
                <p className="step-description">{t('landing.howItWorks.steps.upload.description')}</p>
              </div>

              <div className="step-card">
                <div className="step-icon step-icon-purple">
                  <BoltIcon />
                </div>
                <h3 className="step-title">{t('landing.howItWorks.steps.adapt.title')}</h3>
                <p className="step-description">{t('landing.howItWorks.steps.adapt.description')}</p>
              </div>

              <div className="step-card">
                <div className="step-icon step-icon-green">
                  <CheckCircleIcon />
                </div>
                <h3 className="step-title">{t('landing.howItWorks.steps.review.title')}</h3>
                <p className="step-description">{t('landing.howItWorks.steps.review.description')}</p>
              </div>

              <div className="step-card">
                <div className="step-icon step-icon-yellow">
                  <SendIcon />
                </div>
                <h3 className="step-title">{t('landing.howItWorks.steps.assign.title')}</h3>
                <p className="step-description">{t('landing.howItWorks.steps.assign.description')}</p>
              </div>

              <div className="step-card">
                <div className="step-icon step-icon-pink">
                  <BarChartIcon />
                </div>
                <h3 className="step-title">{t('landing.howItWorks.steps.track.title')}</h3>
                <p className="step-description">{t('landing.howItWorks.steps.track.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Designed for Real Classrooms - Human-centered diversity section */}
        <section className="solutions-section" id="solutions" aria-labelledby="solutions-heading">
          <div className="section-container">
            <div className="section-header">
              <h2 id="solutions-heading" className="section-title">{t('landing.solutions.title')}</h2>
              <p className="section-description">{t('landing.solutions.subtitle')}</p>
            </div>

            <div className="solutions-grid">
              <article className="solution-card solution-card-blue" aria-labelledby="solution-equity">
                <div className="solution-icon solution-icon-blue">
                  <GroupIcon />
                </div>
                <h3 id="solution-equity" className="solution-title">{t('landing.solutions.equity.title')}</h3>
                <p className="solution-description">{t('landing.solutions.equity.description')}</p>
              </article>

              <article className="solution-card solution-card-purple" aria-labelledby="solution-multilingual">
                <div className="solution-icon solution-icon-purple">
                  <PublicIcon />
                </div>
                <h3 id="solution-multilingual" className="solution-title">{t('landing.solutions.multilingual.title')}</h3>
                <p className="solution-description">{t('landing.solutions.multilingual.description')}</p>
              </article>

              <article className="solution-card solution-card-green" aria-labelledby="solution-standards">
                <div className="solution-icon solution-icon-green">
                  <EmojiEventsIcon />
                </div>
                <h3 id="solution-standards" className="solution-title">{t('landing.solutions.standards.title')}</h3>
                <p className="solution-description">{t('landing.solutions.standards.description')}</p>
              </article>

              <article className="solution-card solution-card-yellow" aria-labelledby="solution-wellbeing">
                <div className="solution-icon solution-icon-yellow">
                  <FavoriteIcon />
                </div>
                <h3 id="solution-wellbeing" className="solution-title">{t('landing.solutions.wellbeing.title')}</h3>
                <p className="solution-description">{t('landing.solutions.wellbeing.description')}</p>
              </article>
            </div>

            <div className="solutions-hero-image">
              <img
                src="/images/hispanic-teacher-with-diverse-students-in-classroom.jpg"
                alt={t('landing.solutions.heroImageAlt')}
                className="solutions-image"
              />
            </div>
          </div>
        </section>

        {/* Trusted & Easy to Adopt */}
        <section className="resources-section" id="resources" aria-labelledby="resources-heading">
          <div className="section-container">
            <div className="section-header">
              <h2 id="resources-heading" className="section-title">{t('landing.resources.title')}</h2>
              <p className="section-description">{t('landing.resources.subtitle')}</p>
            </div>

            <div className="resources-grid">
              <div className="resource-item">
                <div className="resource-icon resource-icon-blue">
                  <SchoolIcon />
                </div>
                <h3 className="resource-title">{t('landing.resources.k12.title')}</h3>
                <p className="resource-description">{t('landing.resources.k12.description')}</p>
              </div>

              <div className="resource-item">
                <div className="resource-icon resource-icon-purple">
                  <GroupIcon />
                </div>
                <h3 className="resource-title">{t('landing.resources.educators.title')}</h3>
                <p className="resource-description">{t('landing.resources.educators.description')}</p>
              </div>

              <div className="resource-item">
                <div className="resource-icon resource-icon-slate">
                  <CheckCircleIcon />
                </div>
                <h3 className="resource-title">{t('landing.resources.privacy.title')}</h3>
                <p className="resource-description">{t('landing.resources.privacy.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Loved by Teachers - Testimonial */}
        <section className="testimonial-section" id="pilots" aria-labelledby="testimonial-heading">
          <div className="section-container">
            <div className="section-header">
              <div className="testimonial-header-content">
                <FavoriteIcon className="heart-icon" />
                <h2 id="testimonial-heading" className="section-title">{t('landing.testimonials.title')}</h2>
              </div>
            </div>

            <article className="testimonial-card-featured" aria-label={t('landing.testimonials.review1.author')}>
              <div className="testimonial-author-info">
                <div className="author-avatar author-avatar-purple">
                  {t('landing.testimonials.review1.initials')}
                </div>
                <div className="author-details">
                  <h3 className="author-name">{t('landing.testimonials.review1.author')}</h3>
                  <p className="author-role">{t('landing.testimonials.review1.role')}</p>
                </div>
              </div>
              <blockquote className="testimonial-quote-featured">
                "{t('landing.testimonials.review1.quote')}"
              </blockquote>
              <div className="testimonial-stats">
                <div className="testimonial-stat">
                  <AccessTimeIcon className="stat-icon stat-icon-green" />
                  <span>{t('landing.testimonials.review1.stat1')}</span>
                </div>
                <div className="testimonial-stat">
                  <TrendingUpIcon className="stat-icon stat-icon-blue" />
                  <span>{t('landing.testimonials.review1.stat2')}</span>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* Call to Action */}
        <section className="cta-section" aria-labelledby="cta-heading">
          <div className="section-container">
            <h2 id="cta-heading" className="cta-title">{t('landing.cta.title')}</h2>
            <p className="cta-description">{t('landing.cta.description')}</p>
            <div className="cta-actions">
              <button
                className="cta-button-primary"
                onClick={() => window.open('mailto:sales@classroombridge.com?subject=Contact Sales', '_blank')}
                aria-label={t('landing.cta.contactSales')}
              >
                {t('landing.cta.contactSales')}
              </button>
              <button
                className="cta-button-secondary"
                onClick={() => window.open('mailto:info@classroombridge.com?subject=Schedule a Demo', '_blank')}
                aria-label={t('landing.cta.scheduleDemo')}
              >
                {t('landing.cta.scheduleDemo')}
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="landing-footer" role="contentinfo">
          <div className="footer-container">
            <div className="footer-top">
              <div className="footer-brand">
                <div className="footer-logo" aria-hidden="true">
                  <SchoolIcon sx={{ color: '#fff' }} />
                </div>
                <span className="footer-brand-name">{t('login.title')}</span>
              </div>
              <nav className="footer-nav" aria-label={t('landing.footer.navLabel')}>
                <a href="#about" aria-label={t('landing.footer.about')}>{t('landing.footer.about')}</a>
                <a href="#privacy" aria-label={t('landing.footer.privacy')}>{t('landing.footer.privacy')}</a>
                <a href="#support" aria-label={t('landing.footer.support')}>{t('landing.footer.support')}</a>
                <a href="#contact" aria-label={t('landing.footer.contact')}>{t('landing.footer.contact')}</a>
              </nav>
            </div>
            <div className="footer-bottom">
              <p>{t('landing.footer.copyright')}</p>
            </div>
          </div>
        </footer>
      </main>
    </GoogleOAuthProvider>
  );
};

export default Login;
